/**
 * Step 3 — "Bring in your customer data": multi-select source grid.
 *
 * UX-only: selections are stored in flow state; the actual import wiring
 * comes with the backend hookup.
 */
import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { CheckboxCard, CheckboxGroup } from '@/lib/base/forms/checkbox';
import { SkipButton } from '@/lib/self-service/components/SkipButton';
import { DATA_SOURCE_OPTIONS } from '@/lib/self-service/constants';
import { useSelfServiceFlowState } from '@/lib/self-service/flowState';

interface DataStepProps {
  onContinue: () => void;
}

export function DataStep({ onContinue }: DataStepProps) {
  const { state, updateState } = useSelfServiceFlowState();

  const handleDataSourceChange = useCallback(
    (next: string[]) => updateState({ dataSources: next }),
    [updateState]
  );

  // TODO(self-service): open a free-text "other source" affordance once its
  // design lands.
  const handleOtherSource = useCallback(() => undefined, []);

  return (
    <div className='flex flex-col gap-6'>
      <CheckboxGroup
        value={state.dataSources}
        onChange={handleDataSourceChange}
        aria-label='Customer data sources'
        className='grid grid-cols-1 gap-3 sm:grid-cols-2'
      >
        {DATA_SOURCE_OPTIONS.map(option => (
          <CheckboxCard key={option} value={option} variant='button' size='xl'>
            {option}
          </CheckboxCard>
        ))}
      </CheckboxGroup>

      <Button
        color='link-color'
        size='sm'
        onClick={handleOtherSource}
        className='mx-auto'
      >
        Other source
      </Button>

      <div className='flex flex-col gap-4'>
        <Button size='lg' className='w-full' onClick={onContinue}>
          Continue
        </Button>
        <SkipButton onSkip={onContinue} />
      </div>
    </div>
  );
}
